import { createSlice } from '@reduxjs/toolkit'
import { MINI_APP_IDS, isScopeMatch } from '../../utils/constants/config'

const STORAGE_KEY_CONSENTS = 'mini_app_consents'

export const formatConsentDate = (dateInput = new Date()) => {
  if (!dateInput) return ''
  const date =
    typeof dateInput === 'string' || typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput
  if (isNaN(date.getTime())) return ''
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
  ]
  const d = date.getDate()
  const m = months[date.getMonth()]
  const y = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${d} ${m} ${y}, ${hh}:${mm}`
}

const getStoredConsents = () => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CONSENTS)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    console.error('Error reading stored consents', e)
    return null
  }
}

const saveStoredConsents = (consents) => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY_CONSENTS, JSON.stringify(consents))
  } catch (e) {
    console.error('Error saving consents', e)
  }
}

const initialStored = getStoredConsents()
const initialHistory = []

const initialMiniApps = {
  [MINI_APP_IDS.CREATE]: {
    id: MINI_APP_IDS.CREATE,
    name: 'Eros Create',
    dotColor: '#E05285',
    isOn: initialStored?.[MINI_APP_IDS.CREATE]?.isOn ?? false,
    hasAgreed: initialStored?.[MINI_APP_IDS.CREATE]?.hasAgreed ?? false,
    statusText:
      initialStored?.[MINI_APP_IDS.CREATE]?.statusText || 'Off · not agreed yet',
    hasOptionalConsent:
      initialStored?.[MINI_APP_IDS.CREATE]?.hasOptionalConsent ?? false,
    optionalStatusText:
      initialStored?.[MINI_APP_IDS.CREATE]?.optionalStatusText || 'Off',
    consentId: initialStored?.[MINI_APP_IDS.CREATE]?.consentId || null,
    optionalConsentId: initialStored?.[MINI_APP_IDS.CREATE]?.optionalConsentId || null,
    agreedAt: initialStored?.[MINI_APP_IDS.CREATE]?.agreedAt || null
  },
  [MINI_APP_IDS.WELLNESS]: {
    id: MINI_APP_IDS.WELLNESS,
    name: 'Eros Wellness',
    dotColor: '#2ED3B7',
    isOn: initialStored?.[MINI_APP_IDS.WELLNESS]?.isOn ?? false,
    hasAgreed: initialStored?.[MINI_APP_IDS.WELLNESS]?.hasAgreed ?? false,
    statusText:
      initialStored?.[MINI_APP_IDS.WELLNESS]?.statusText || 'Off · not agreed yet',
    hasOptionalConsent:
      initialStored?.[MINI_APP_IDS.WELLNESS]?.hasOptionalConsent ?? false,
    optionalStatusText:
      initialStored?.[MINI_APP_IDS.WELLNESS]?.optionalStatusText || 'Off',
    consentId: initialStored?.[MINI_APP_IDS.WELLNESS]?.consentId || null,
    optionalConsentId: initialStored?.[MINI_APP_IDS.WELLNESS]?.optionalConsentId || null,
    agreedAt: initialStored?.[MINI_APP_IDS.WELLNESS]?.agreedAt || null
  },
  [MINI_APP_IDS.CONSOLE]: {
    id: MINI_APP_IDS.CONSOLE,
    name: 'Eros Console',
    dotColor: '#00B8F8',
    isOn: initialStored?.[MINI_APP_IDS.CONSOLE]?.isOn ?? false,
    hasAgreed: initialStored?.[MINI_APP_IDS.CONSOLE]?.hasAgreed ?? false,
    statusText:
      initialStored?.[MINI_APP_IDS.CONSOLE]?.statusText || 'Off · not agreed yet',
    hasOptionalConsent:
      initialStored?.[MINI_APP_IDS.CONSOLE]?.hasOptionalConsent ?? false,
    optionalStatusText:
      initialStored?.[MINI_APP_IDS.CONSOLE]?.optionalStatusText || 'Off',
    consentId: initialStored?.[MINI_APP_IDS.CONSOLE]?.consentId || null,
    optionalConsentId: initialStored?.[MINI_APP_IDS.CONSOLE]?.optionalConsentId || null,
    agreedAt: initialStored?.[MINI_APP_IDS.CONSOLE]?.agreedAt || null
  }
}

const initialState = {
  miniApps: initialMiniApps,
  history: initialHistory,
  modal: {
    isOpen: false,
    miniAppId: MINI_APP_IDS.CREATE,
    isModalBlocking: false,
    actionTriggered: null
  },
  withdrawModal: {
    isOpen: false,
    miniAppId: null
  }
}

const consentSlice = createSlice({
  name: 'consent',
  initialState,
  reducers: {
    openConsentModal: (state, action) => {
      const {
        miniAppId = MINI_APP_IDS.CREATE,
        isModalBlocking = false,
        actionTriggered = null,
        redirectUrl = null,
        defaultOptionalChecked = false
      } = action.payload || {}
      state.modal = {
        isOpen: true,
        miniAppId,
        isModalBlocking,
        actionTriggered,
        redirectUrl,
        defaultOptionalChecked
      }
    },
    closeConsentModal: (state) => {
      state.modal.isOpen = false
      state.modal.actionTriggered = null
      state.modal.redirectUrl = null
      state.modal.defaultOptionalChecked = false
    },
    openWithdrawModal: (state, action) => {
      const miniAppId =
        typeof action.payload === 'string'
          ? action.payload
          : action.payload?.miniAppId
      state.withdrawModal = {
        isOpen: true,
        miniAppId
      }
    },
    closeWithdrawModal: (state) => {
      state.withdrawModal.isOpen = false
      state.withdrawModal.miniAppId = null
    },
    acceptConsent: (state, action) => {
      const {
        miniAppId = MINI_APP_IDS.CREATE,
        optionalChecked = false,
        consentId = null,
        optionalConsentId = null
      } = action.payload || {}
      const formattedDate = formatConsentDate(new Date())
      const ver = action.payload?.version
        ? (action.payload.version.startsWith('v') ? action.payload.version : `v${action.payload.version}`)
        : 'v1.0'

      if (state.miniApps[miniAppId]) {
        state.miniApps[miniAppId].isOn = true
        state.miniApps[miniAppId].hasAgreed = true
        state.miniApps[miniAppId].agreedAt = formattedDate
        state.miniApps[miniAppId].statusText = ver
          ? `On · agreed ${ver} · ${formattedDate}`
          : `On · agreed ${formattedDate}`
        state.miniApps[miniAppId].hasOptionalConsent = !!optionalChecked
        state.miniApps[miniAppId].optionalStatusText = optionalChecked
          ? (ver
              ? `On · agreed ${ver} · ${formattedDate}`
              : `On · agreed ${formattedDate}`)
          : 'Off'
        if (consentId) state.miniApps[miniAppId].consentId = consentId
        if (optionalConsentId) state.miniApps[miniAppId].optionalConsentId = optionalConsentId
        state.miniApps[miniAppId].withdrawnAt = null
      }

      saveStoredConsents(state.miniApps)

      state.modal.isOpen = false
    },
    withdrawConsent: (state, action) => {
      const miniAppId =
        typeof action.payload === 'string'
          ? action.payload
          : action.payload?.miniAppId || MINI_APP_IDS.CREATE

      if (state.miniApps[miniAppId]) {
        state.miniApps[miniAppId].isOn = false
        state.miniApps[miniAppId].hasAgreed = false
        state.miniApps[miniAppId].consentId = null
        state.miniApps[miniAppId].optionalConsentId = null
        state.miniApps[miniAppId].statusText = 'Off · not agreed yet'
        state.miniApps[miniAppId].hasOptionalConsent = false
        state.miniApps[miniAppId].optionalStatusText = 'Off'
        state.miniApps[miniAppId].withdrawnAt = Date.now()
      }

      saveStoredConsents(state.miniApps)

      state.withdrawModal.isOpen = false
      state.withdrawModal.miniAppId = null
    },
    resetConsents: (state) => {
      Object.keys(state.miniApps).forEach((appId) => {
        if (state.miniApps[appId]) {
          state.miniApps[appId].isOn = false
          state.miniApps[appId].hasAgreed = false
          state.miniApps[appId].consentId = null
          state.miniApps[appId].optionalConsentId = null
          state.miniApps[appId].statusText = 'Off · not agreed yet'
          state.miniApps[appId].hasOptionalConsent = false
          state.miniApps[appId].optionalStatusText = 'Off'
          state.miniApps[appId].agreedAt = null
          state.miniApps[appId].withdrawnAt = null
        }
      })
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY_CONSENTS)
      }
    },
    toggleOptionalConsent: (state, action) => {
      const {
        miniAppId,
        enabled,
        optionalConsentId = null,
        version = null,
        date = null
      } = action.payload || {}
      if (state.miniApps[miniAppId]) {
        state.miniApps[miniAppId].hasOptionalConsent = enabled
        if (enabled) {
          const formattedDate = date || formatConsentDate(new Date())
          const ver = version
            ? (version.startsWith('v') ? version : `v${version}`)
            : (state.miniApps[miniAppId]?.version || 'v1.0')
          state.miniApps[miniAppId].optionalStatusText = ver
            ? `On · agreed ${ver} · ${formattedDate}`
            : `On · agreed ${formattedDate}`
        } else {
          state.miniApps[miniAppId].optionalStatusText = 'Off'
        }
        if (optionalConsentId) {
          state.miniApps[miniAppId].optionalConsentId = optionalConsentId
        } else if (!enabled) {
          state.miniApps[miniAppId].optionalConsentId = null
        }
        saveStoredConsents(state.miniApps)
      }
    },
    syncMiniAppsFromHistoryData: (state, action) => {
      const historyItems = action.payload || []
      if (!Array.isArray(historyItems)) return

      const sorted = [...historyItems].sort((a, b) => {
        const timeA = new Date(b.updated_at || b.created_at || b.timestamp || b.revoked_at || 0).getTime()
        const timeB = new Date(a.updated_at || a.created_at || a.timestamp || a.revoked_at || 0).getTime()
        return timeA - timeB
      })

      const isAcceptedAction = (actionStr) => {
        const upper = actionStr?.toUpperCase()
        return upper === 'ACCEPTED' || upper === 'REACCEPTED'
      }

      Object.keys(state.miniApps).forEach((appId) => {
        const appState = state.miniApps[appId]
        if (!appState) return

        const defaultVer = 'v1.0'

        const latestMandatory = sorted.find(
          (item) =>
            isScopeMatch(item.scope, appId) &&
            item.consent_type?.toUpperCase() === 'MANDATORY'
        )

        const mandatoryTime = latestMandatory
          ? new Date(latestMandatory.updated_at || latestMandatory.created_at || latestMandatory.timestamp || 0).getTime()
          : 0
        const isWithdrawnLocally =
          appState.withdrawnAt && mandatoryTime <= appState.withdrawnAt

        if (
          !isWithdrawnLocally &&
          latestMandatory &&
          isAcceptedAction(latestMandatory.action)
        ) {
          const ver = latestMandatory.consent_version
            ? (latestMandatory.consent_version.startsWith('v')
                ? latestMandatory.consent_version
                : `v${latestMandatory.consent_version}`)
            : defaultVer
          const formattedDate = formatConsentDate(latestMandatory.created_at)
          appState.isOn = true
          appState.hasAgreed = true
          appState.consentId = latestMandatory.consent_id || latestMandatory.id
          appState.agreedAt = formattedDate
          appState.statusText = ver
            ? `On · agreed ${ver} · ${formattedDate}`
            : `On · agreed ${formattedDate}`
        } else {
          appState.isOn = false
          appState.hasAgreed = false
          appState.consentId = null
          appState.statusText = 'Off · not agreed yet'
        }

        const latestOptional = sorted.find(
          (item) =>
            isScopeMatch(item.scope, appId) &&
            (item.consent_type?.toUpperCase() === 'OPTIONAL' ||
              item.consent_type?.toUpperCase() === 'PERSONALISATION')
        )

        if (
          !isWithdrawnLocally &&
          latestOptional &&
          isAcceptedAction(latestOptional.action)
        ) {
          const optVer = latestOptional.consent_version
            ? (latestOptional.consent_version.startsWith('v')
                ? latestOptional.consent_version
                : `v${latestOptional.consent_version}`)
            : defaultVer
          const formattedOptDate = formatConsentDate(
            latestOptional.created_at || latestOptional.timestamp
          )
          appState.hasOptionalConsent = true
          appState.optionalConsentId = latestOptional.consent_id || latestOptional.id
          appState.optionalStatusText = appState.isOn
            ? (optVer
                ? `On · agreed ${optVer} · ${formattedOptDate}`
                : `On · agreed ${formattedOptDate}`)
            : 'Off'
        } else {
          appState.hasOptionalConsent = false
          appState.optionalConsentId = null
          appState.optionalStatusText = 'Off'
        }
      })

      saveStoredConsents(state.miniApps)
    },
    syncConsentsFromHistory: (state, action) => {
      const historyItems = action.payload || []
      if (!Array.isArray(historyItems)) return

      // Sort history descending by created_at
      const sorted = [...historyItems].sort((a, b) => {
        const timeA = new Date(a.created_at || a.timestamp || 0).getTime()
        const timeB = new Date(b.created_at || b.timestamp || 0).getTime()
        return timeB - timeA
      })

      const isAcceptedAction = (actionStr) => {
        const upper = actionStr?.toUpperCase()
        return upper === 'ACCEPTED' || upper === 'REACCEPTED'
      }

      Object.keys(state.miniApps).forEach((appId) => {
        const appState = state.miniApps[appId]
        if (!appState) return

        const defaultVer = 'v1.0'

        // Latest mandatory record
        const latestMandatory = sorted.find(
          (item) =>
            isScopeMatch(item.scope, appId) &&
            item.consent_type?.toUpperCase() === 'MANDATORY'
        )

        if (latestMandatory && isAcceptedAction(latestMandatory.action)) {
          const ver = latestMandatory.consent_version
            ? (latestMandatory.consent_version.startsWith('v')
                ? latestMandatory.consent_version
                : `v${latestMandatory.consent_version}`)
            : defaultVer
          const formattedDate = formatConsentDate(latestMandatory.created_at)
          appState.isOn = true
          appState.hasAgreed = true
          appState.consentId = latestMandatory.consent_id || latestMandatory.id
          appState.agreedAt = formattedDate
          appState.statusText = ver
            ? `On · agreed ${ver} · ${formattedDate}`
            : `On · agreed ${formattedDate}`
        } else if (latestMandatory && latestMandatory.action?.toUpperCase() === 'REVOKED') {
          appState.isOn = false
          appState.hasAgreed = false
          appState.consentId = null
          appState.statusText = 'Off · not agreed yet'
        } else {
          appState.isOn = false
          appState.hasAgreed = false
          appState.consentId = null
          appState.statusText = 'Off · not agreed yet'
        }

        // Latest optional record
        const latestOptional = sorted.find(
          (item) =>
            isScopeMatch(item.scope, appId) &&
            (item.consent_type?.toUpperCase() === 'OPTIONAL' ||
              item.consent_type?.toUpperCase() === 'PERSONALISATION')
        )

        if (latestOptional && isAcceptedAction(latestOptional.action)) {
          const optVer = latestOptional.consent_version
            ? (latestOptional.consent_version.startsWith('v')
                ? latestOptional.consent_version
                : `v${latestOptional.consent_version}`)
            : defaultVer
          const formattedOptDate = formatConsentDate(
            latestOptional.created_at || latestOptional.timestamp
          )
          appState.hasOptionalConsent = true
          appState.optionalConsentId = latestOptional.consent_id || latestOptional.id
          appState.optionalStatusText = appState.isOn
            ? (optVer
                ? `On · agreed ${optVer} · ${formattedOptDate}`
                : `On · agreed ${formattedOptDate}`)
            : 'Off'
        } else if (latestOptional && latestOptional.action?.toUpperCase() === 'REVOKED') {
          appState.hasOptionalConsent = false
          appState.optionalConsentId = null
          appState.optionalStatusText = 'Off'
        } else {
          appState.hasOptionalConsent = false
          appState.optionalConsentId = null
          appState.optionalStatusText = 'Off'
        }
      })

      // Format history list
      state.history = sorted.map((item) => {
        const actionUpper = item.action?.toUpperCase()
        const isAccepted = isAcceptedAction(item.action)
        const actionWord =
          actionUpper === 'REACCEPTED'
            ? 'Re-accepted'
            : isAccepted
              ? 'Accepted'
              : 'Withdrew'
        const appName =
          isScopeMatch(item.scope, MINI_APP_IDS.CREATE)
            ? 'Eros Create'
            : isScopeMatch(item.scope, MINI_APP_IDS.WELLNESS)
              ? 'Eros Wellness'
              : isScopeMatch(item.scope, MINI_APP_IDS.CONSOLE)
                ? 'Eros Console'
                : item.scope || 'App'
        const isOptional =
          item.consent_type?.toUpperCase() === 'OPTIONAL' ||
          item.consent_type?.toUpperCase() === 'PERSONALISATION'
        const ver = item.consent_version
          ? (item.consent_version.startsWith('v')
              ? item.consent_version
              : `v${item.consent_version}`)
          : 'v1.0'

        const description =
          item.description ||
          (isOptional
            ? `${actionWord} ${appName} optional consent ${ver}`
            : `${actionWord} ${appName} ${isAccepted ? 'notice' : 'consent'} ${ver}`)

        return {
          id: item.id || `${item.consent_id}-${item.action}-${item.created_at}`,
          consent_id: item.consent_id || item.id,
          scope: item.scope,
          consent_type: item.consent_type,
          action: item.action,
          dateText: formatConsentDate(item.created_at || item.timestamp),
          description,
          raw: item
        }
      })

      saveStoredConsents(state.miniApps)
    }
  }
})

export const {
  openConsentModal,
  closeConsentModal,
  openWithdrawModal,
  closeWithdrawModal,
  acceptConsent,
  withdrawConsent,
  resetConsents,
  toggleOptionalConsent,
  syncConsentsFromHistory,
  syncMiniAppsFromHistoryData
} = consentSlice.actions

export { MINI_APP_IDS }

export default consentSlice.reducer
